import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { primaryColor } from '../styles/GlobalStyles';
import { storeCSRFToken, getCSRFToken, sanitizeInput } from '../utils/security';

const FooterContainer = styled.footer`
  background-color: #222;
  color: #fff;
  padding: 4rem 0 3rem;
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(to right, ${primaryColor}, #85c1ff);
  }
`;

const FooterContent = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 3rem;
  
  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const Copyright = styled.div`
  font-size: 0.95rem;
  line-height: 1.6;
  animation: fadeIn 0.8s ease-out;
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  a {
    color: #99d0ff;
    transition: all 0.3s ease;
    position: relative;
    
    &:after {
      content: '';
      position: absolute;
      bottom: -2px;
      left: 0;
      width: 0;
      height: 1px;
      background-color: #99d0ff;
      transition: width 0.3s ease;
    }
    
    &:hover {
      color: #fff;
      text-decoration: none;
      
      &:after {
        width: 100%;
      }
    }
  }
`;

const ContactForm = styled.div`
  animation: slideIn 0.8s ease-out;
  
  @keyframes slideIn {
    from { opacity: 0; transform: translateX(20px); }
    to { opacity: 1; transform: translateX(0); }
  }
  
  h3 {
    margin-bottom: 1.5rem;
    font-size: 1.6rem;
    font-weight: 600;
    background: linear-gradient(to right, ${primaryColor}, #85c1ff);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    display: inline-block;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const InputWrapper = styled.div`
  position: relative;
  overflow: hidden;
`;

const Input = styled.input`
  padding: 0.9rem 1rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  font-size: 1rem;
  background-color: rgba(255, 255, 255, 0.05);
  color: #fff;
  width: 100%;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: ${primaryColor};
    background-color: rgba(255, 255, 255, 0.1);
    box-shadow: 0 0 0 3px rgba(66, 141, 231, 0.2);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
    transition: all 0.3s ease;
  }
  
  &:focus::placeholder {
    opacity: 0.7;
    transform: translateX(5px);
  }
`;

const Textarea = styled.textarea`
  padding: 0.9rem 1rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  font-size: 1rem;
  background-color: rgba(255, 255, 255, 0.05);
  color: #fff;
  min-height: 140px;
  resize: vertical;
  width: 100%;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: ${primaryColor};
    background-color: rgba(255, 255, 255, 0.1);
    box-shadow: 0 0 0 3px rgba(66, 141, 231, 0.2);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
    transition: all 0.3s ease;
  }
  
  &:focus::placeholder {
    opacity: 0.7;
    transform: translateX(5px);
  }
`;

const Button = styled.button`
  padding: 0.9rem;
  background-color: ${primaryColor};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(to right, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: all 0.5s ease;
  }
  
  &:hover {
    background-color: #3276ca;
    transform: translateY(-3px);
    box-shadow: 0 6px 20px rgba(50, 118, 202, 0.4);
    
    &:after {
      left: 100%;
    }
  }
  
  &:active {
    transform: translateY(-1px);
  }
`;

const SuccessMessage = styled.div`
  background-color: rgba(75, 181, 67, 0.15);
  color: #4bb543;
  padding: 1rem;
  border-radius: 8px;
  margin-top: 1rem;
  border-left: 4px solid #4bb543;
  animation: fadeInUp 0.4s ease-out;
  
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
  
  a {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background-color: rgba(255, 255, 255, 0.1);
    color: #fff;
    transition: all 0.3s ease;
    
    &:hover {
      background-color: ${primaryColor};
      transform: translateY(-3px);
    }
  }
`;

const Footer = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [csrfToken, setCsrfToken] = useState('');
  
  // Tạo CSRF token khi component mount
  useEffect(() => {
    const token = getCSRFToken() || storeCSRFToken();
    setCsrfToken(token);
  }, []);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Làm sạch dữ liệu đầu vào
    const sanitizedData = {
      name: sanitizeInput(formData.name),
      email: sanitizeInput(formData.email),
      message: sanitizeInput(formData.message)
    };
    
    // Kiểm tra CSRF token
    if (!csrfToken) {
      console.error('Security Error: CSRF token is missing');
      return;
    }
    
    // Construct mailto link with sanitized form data
    const subject = `Message from ${sanitizedData.name} via Mission Manager Website`;
    const body = `Name: ${sanitizedData.name}\nEmail: ${sanitizedData.email}\n\nMessage: ${sanitizedData.message}`;
    const mailtoLink = `mailto:hsolutiondeveloper@gmail.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    
    // Open default email client
    window.open(mailtoLink);
    
    // Reset the form and show success message
    setFormData({ name: '', email: '', message: '' });
    setIsSubmitted(true);
    
    // Hide the success message after 3 seconds
    setTimeout(() => {
      setIsSubmitted(false);
    }, 3000);
  };

  return (
    <FooterContainer>
      <div className="container">
        <FooterContent>
          <Copyright>
            <h3 style={{ marginBottom: '1.2rem', fontWeight: 600, fontSize: '1.6rem' }}>Mission Manager</h3>
            <p>Helping you manage your tasks and boost productivity in a simple yet powerful way. Get organized and stay on top of your mission.</p>
            <p style={{ marginTop: '1.5rem' }}>Copyright © Mission Manager 2025</p>
            <p><Link to="/privacy-policy">Privacy Policy</Link> • <Link to="/terms-of-service">Terms of service</Link></p>
            <p style={{ marginTop: '1.2rem' }}>Contact: <a href="mailto:hsolutiondeveloper@gmail.com">hsolutiondeveloper@gmail.com</a></p>
            
            <SocialLinks>
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm3 8h-1.35c-.538 0-.65.221-.65.778v1.222h2l-.209 2h-1.791v7h-3v-7h-2v-2h2v-2.308c0-1.769.931-2.692 3.029-2.692h1.971v3z"/>
                </svg>
              </a>
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" aria-label="Twitter">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm6.066 9.645c.183 4.04-2.83 8.544-8.164 8.544-1.622 0-3.131-.476-4.402-1.291 1.524.18 3.045-.244 4.252-1.189-1.256-.023-2.317-.854-2.684-1.995.451.086.895.061 1.298-.049-1.381-.278-2.335-1.522-2.304-2.853.388.215.83.344 1.301.359-1.279-.855-1.641-2.544-.889-3.835 1.416 1.738 3.533 2.881 5.92 3.001-.419-1.796.944-3.527 2.799-3.527.825 0 1.572.349 2.096.907.654-.128 1.27-.368 1.824-.697-.215.671-.67 1.233-1.263 1.589.581-.07 1.135-.224 1.649-.453-.384.578-.87 1.084-1.433 1.489z"/>
                </svg>
              </a>
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm-2 16h-2v-6h2v6zm-1-6.891c-.607 0-1.1-.496-1.1-1.109 0-.612.492-1.109 1.1-1.109s1.1.497 1.1 1.109c0 .613-.493 1.109-1.1 1.109zm8 6.891h-1.998v-2.861c0-1.881-2.002-1.722-2.002 0v2.861h-2v-6h2v1.093c.872-1.616 4-1.736 4 1.548v3.359z"/>
                </svg>
              </a>
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
            </SocialLinks>
          </Copyright>
          
          <ContactForm>
            <h3>Send a Message</h3>
            <Form onSubmit={handleSubmit}>
              {/* CSRF Token hidden field */}
              <input 
                type="hidden" 
                name="csrf_token" 
                value={csrfToken}
              />
              
              <InputWrapper>
                <Input 
                  type="text" 
                  placeholder="Your Name" 
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required 
                  maxLength={100}
                  pattern="[A-Za-z0-9\s\-_']+"
                  title="Please enter a valid name without special characters"
                />
              </InputWrapper>
              <InputWrapper>
                <Input 
                  type="email" 
                  placeholder="Your Email" 
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required 
                  maxLength={150}
                />
              </InputWrapper>
              <InputWrapper>
                <Textarea 
                  placeholder="Your Message" 
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  maxLength={1000}
                />
              </InputWrapper>
              <Button type="submit">
                Send Message
              </Button>
            </Form>
            
            {isSubmitted && (
              <SuccessMessage>
                Thank you for your message! We will get back to you soon.
              </SuccessMessage>
            )}
          </ContactForm>
        </FooterContent>
      </div>
    </FooterContainer>
  );
};

export default Footer; 